import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

const h2Animation = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const slideAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.5 },
};

export default function Testimonials() {
  const { t } = useLanguage();
  const items = t("testimonials.items");
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    if (Array.isArray(items)) setCurrent((c) => (c + 1) % items.length);
  }, [items]);

  const prev = useCallback(() => {
    if (Array.isArray(items)) setCurrent((c) => (c - 1 + items.length) % items.length);
  }, [items]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  if (!Array.isArray(items)) return null;

  return (
    <section data-testid="testimonials-section" className="relative py-16 md:py-20 bg-[#131b2f]">
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.h2
          {...h2Animation}
          data-testid="testimonials-h2"
          className="font-playfair text-4xl md:text-5xl text-[#f8f9fa] tracking-tight text-center mb-16"
        >
          {t("testimonials.h2")}
        </motion.h2>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              {...slideAnimation}
              className="text-center"
            >
              <Quote className="mx-auto text-[#1a6cf5]/30 mb-8" size={48} />
              <blockquote
                data-testid={`testimonial-quote-${current}`}
                className="font-playfair text-xl md:text-2xl text-[#f8f9fa] leading-relaxed mb-8 max-w-3xl mx-auto italic"
              >
                "{items[current].quote}"
              </blockquote>
              <div>
                <p
                  data-testid={`testimonial-author-${current}`}
                  className="font-outfit text-base text-[#f8f9fa] font-medium"
                >
                  {items[current].author}
                </p>
                <p className="font-outfit text-xs tracking-[0.2em] text-[#1a6cf5] mt-1 uppercase">
                  {items[current].role}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-12">
            <button
              onClick={prev}
              data-testid="testimonial-prev"
              className="p-3 rounded-full border border-[#2a3449] text-[#94a3b8] hover:text-white hover:border-[#1a6cf5]/50 transition-all"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex gap-2">
              {items.map((item, i) => (
                <button
                  key={item.author}
                  onClick={() => setCurrent(i)}
                  data-testid={`testimonial-dot-${i}`}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    i === current ? "bg-[#1a6cf5] w-8" : "bg-[#2a3449]"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={next}
              data-testid="testimonial-next"
              className="p-3 rounded-full border border-[#2a3449] text-[#94a3b8] hover:text-white hover:border-[#1a6cf5]/50 transition-all"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
